<template>
  <div>
    <v-card-text>
      <v-card-title>
        Team Information
      </v-card-title>
      <v-card-subtitle>
        <p>
          <strong>{{ team.name }}</strong>
        </p>
        <p>
          {{ team.description }}
        </p>
      </v-card-subtitle>
      <v-card-subtitle>
        <p>
          <strong>{{ team.leader.name }}</strong> (Team leader)
        </p>
        <p
          v-for="(member) in team.members"
          :key="`member-${formatMemberEmail(member.email)}`"
        >
          {{ member.name }}
        </p>
      </v-card-subtitle>
      <v-card-title>
        Vote
      </v-card-title>
      <div class="voting-wrapper">
        <v-container>
          <v-row
            v-for="(category, index) in categories"
            :key="category.name"
            class="category-row"
            :class="{
              'odd': index % 2 !== 0
            }"
          >
            <v-col
              cols="2"
              class="category-name"
            >
              {{ category.name }}
            </v-col>
            <v-col
              cols="10"
            >
              <v-container fluid>
                <v-row>
                  <v-radio-group
                    v-model="calculatedScores[index]"
                    row
                  >
                    <v-col
                      v-for="(criteria, cIndex) in category.scoreHelpers"
                      :key="`score-${cIndex}`"
                    >
                      <v-radio
                        :value="cIndex"
                      />
                      <v-card-subtitle>
                        {{ criteria }}
                      </v-card-subtitle>
                    </v-col>
                  </v-radio-group>
                </v-row>
              </v-container>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </v-card-text>

    <v-divider />

    <v-card-actions>
      <v-spacer />
      <v-btn
        color="primary"
        class="cancel-button"
        text
        @click="closeDialog()"
      >
        Cancel
      </v-btn>
      <v-btn
        v-if="!haveAlreadyVotedOnThisTeam"
        color="primary"
        text
        class="submit-button"
        @click="submitVote()"
      >
        Submit
      </v-btn>
      <v-btn
        v-else
        color="primary"
        text
        class="update-button"
        @click="updateVote()"
      >
        Update
      </v-btn>
    </v-card-actions>
  </div>
</template>

<script>
import VotingClient from '@/api/VotingClient';
import { mapGetters } from 'vuex';

export default {
  name: 'TeamVote',
  props: {
    column: {
      type: [Object, Array],
      default: () => {},
    },
    currentUser: {
      type: Object,
      default: () => {},
    },
    eventState: {
      type: String,
      default: '',
    },
    row: {
      type: [Object, Array],
      default: () => {},
    },
    teamsCount: {
      type: Number,
      default: 0,
    },
    team: {
      type: Object,
      default: () => ({
      }),
    },
  },
  data() {
    return {
      categories: this.$store.state.categories,
      haveTeam: this.currentUser.team !== '',
      teamDescription: this.team.description,
      teamName: this.team.name,
      // add a placeholder value to hold each category's score
      scores: this.$store.state.categories.map(() => 2),
    };
  },
  computed: {
    ...mapGetters({
      currentJwtToken: 'currentJwtToken',
      currentHackathonId: 'currentHackathonId',
    }),
    thisTeamHasAVote() {
      return this.$store.state.votes.filter((vote) => vote.team.id === this.team.id).length > 0;
    },
    haveAlreadyVotedOnThisTeam() {
      return this.thisTeamHasAVote;
    },
    calculatedScores() {
      if (this.haveAlreadyVotedOnThisTeam) {
        const teamVote = this.$store.state.votes.filter((vote) => vote.team.id === this.team.id)[0];
        // update scores to have the previous team vote values
        this.setTeamVoteId(teamVote.id);
        const updatedScores = [
          teamVote.awesomeness,
          teamVote.innovation,
          teamVote.completeness,
          teamVote.utility,
          teamVote.difficulty,
        ];
        this.setNewScores(updatedScores);
        return updatedScores;
      }
      return this.$store.state.categories.map(() => 2);
    },
  },
  methods: {
    setTeamVoteId(voteId) {
      this.team.voteId = voteId;
    },
    setNewScores(newScoresArray) {
      this.scores = newScoresArray;
    },
    closeDialog() {
      this.$emit('closeDialog');
    },
    formatMemberEmail(memberEmail) {
      try {
        return memberEmail.replace('@', '-').replace('.', '');
      } catch (err) {
        return memberEmail;
      }
    },
    async submitVote() {
      const voteInformation = {
        teamId: this.team.id,
        awesomeness: this.calculatedScores[0],
        innovation: this.calculatedScores[1],
        completeness: this.calculatedScores[2],
        utility: this.calculatedScores[3],
        difficulty: this.calculatedScores[4],
      };
      try {
        /* eslint-disable-next-line max-len */
        const votingClient = new VotingClient(this.currentJwtToken, this.currentHackathonId);
        await votingClient.submitVote(voteInformation);
        this.closeDialog();
        this.$router.go();
      } catch (err) {
        /* eslint-disable-next-line no-alert */
        alert('Unable to submit your vote. Please reach out to an event admin for more information.');
      }
      this.closeDialog();
    },
    async updateVote() {
      const voteInformation = {
        teamId: this.team.id,
        awesomeness: this.scores[0],
        innovation: this.scores[1],
        completeness: this.scores[2],
        utility: this.scores[3],
        difficulty: this.scores[4],
        voteId: this.team.voteId || '',
      };
      try {
        /* eslint-disable-next-line max-len */
        const votingClient = new VotingClient(this.currentJwtToken, this.currentHackathonId);
        await votingClient.updateVote(voteInformation);
        this.closeDialog();
        this.$router.go();
      } catch (err) {
        /* eslint-disable-next-line no-alert */
        alert('Unable to edit your vote. Please reach out to an event admin for more information.');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.v-card__title {
  padding-left: .5em;
  padding-right: .5em;
  color: #f26522;
}

.v-card__subtitle {
  padding-left: .75em;
  padding-right: .75em;
}

.v-btn {
  color: #004e7d;
  text-transform: none;
}

.submit-button {
  text-transform: none;
  background-color: #004e7d !important;
  color: #fff !important;
}

.cancel-button {
  text-transform: none;
  color: #004e7d;
  margin-right: 1em;
}

.v-input--selection-controls.v-input {
  flex: auto;
}

.voting-wrapper .row,
.voting-wrapper .col,
.voting-wrapper .v-input--selection-controls.v-input {
  padding-bottom: 0;
  padding-top:0;
}

.voting-wrapper .v-card__subtitle {
  padding-bottom: 0;
  padding-left: 0;
  padding-right: 0;
  text-align: center;
}

.category-name {
  text-transform: capitalize;
  font-weight: 600;
  color: #f26522;
  padding-top: 1em !important;
}

.category-row.odd {
  background-color: #f2f2f2;
}

.v-radio {
  margin-left: 40%;
}
</style>
